import { useEffect, useRef, useState } from 'react';

import { heatCircles, HeatCell } from './heatGrid';

/**
 * Desenha a densidade no mapa (MV2-1706).
 *
 * Círculos concêntricos por célula, do núcleo da API. Retângulo foi a primeira tentativa e
 * ficou com cara de tabuleiro: borda reta corta seco, e densidade não tem quina. Três anéis
 * translúcidos por célula fazem a intensidade cair devagar, e vizinhas se emendam. O Heatmap Layer, que fazia isso com borrão,
 * foi removido da Maps JavaScript API na versão 3.65 — a mensagem do próprio Google era
 * "The Heatmap Layer functionality in the Maps JavaScript API is no longer available as of
 * version 3.65" — e a biblioteca do dashboard pede `v=weekly`.
 *
 * A criação fica dentro de try/catch, e não à mercê do React: componente que cria objeto do
 * Google dentro de efeito faz a exceção subir como erro não capturado, e vindo de script de
 * outra origem o navegador entrega ao React apenas "A cross-origin error was thrown", sem
 * mensagem. Foi assim que a tela ficou branca antes de existir esta camada — e foi o
 * try/catch que finalmente mostrou a frase do Google.
 */

export type HeatGridResult = {
  /** Mensagem do erro do Google, ou null quando a grade está desenhada. */
  error: string | null;
};

export const useHeatGridLayer = (map: unknown, cells: HeatCell[] | null): HeatGridResult => {
  const [error, setError] = useState<string | null>(null);

  const drawn = useRef<any[]>([]);

  useEffect(() => {
    const maps = (window as any).google?.maps;

    if (!map || !cells || cells.length === 0 || !maps?.Circle) {
      return undefined;
    }

    /**
     * Tira do mapa o que esta na lista.
     *
     * Os círculos não somem com o React: quem os desenhou foi o Google, e só
     * setMap(null) os apaga.
     */
    const clear = () => {
      drawn.current.forEach((circle) => {
        try {
          circle.setMap(null);
        } catch {
          // Mapa já destruído: não há o que desfazer.
        }
      });

      drawn.current = [];
    };

    try {
      // Guarda cada círculo ao criá-lo, e não a lista pronta no fim: o construtor do
      // Circle já prende o objeto ao mapa, então uma exceção no meio do lote deixava os
      // anteriores pintados e sem referência — a limpeza varria a lista antiga, trocar
      // para Pontos não apagava nada, e cada mudança de zoom empilhava outro lote
      // parcial por cima.
      heatCircles(cells).forEach((ring) => {
        drawn.current.push(
          new maps.Circle({
            map,
            center: ring.center,
            radius: ring.radius,
            fillColor: ring.color,
            fillOpacity: ring.opacity,
            // Sem borda: o traço é justamente o que fazia a célula parecer quina.
            strokeWeight: 0,
            // Não recebe clique: quem clica no mapa quer a estação, não a mancha.
            clickable: false,
            zIndex: ring.zIndex,
          }),
        );
      });

      setError(null);
    } catch (failure) {
      // Meia grade desenhada informa menos que grade nenhuma, e a mensagem de erro
      // aparece na tela: apaga o lote parcial em vez de esperar a próxima limpeza.
      clear();

      // eslint-disable-next-line no-console
      console.error('Falha ao desenhar a densidade', {
        erro: failure,
        mensagem: (failure as Error)?.message,
        celulas: cells.length,
      });

      setError((failure as Error)?.message || String(failure));
    }

    return clear;
  }, [map, cells]);

  return { error };
};
